import React, { useEffect, useState, useCallback } from "react";
import { serviceApi } from "../../services/api";
import api from "../../services/api";
import { Badge, Button, Card, PageHeader, Input } from "../../components/ui";
import { showToast } from "../../utils/toast";
import {
  Globe,
  ChevronDown,
  ChevronUp,
  Smartphone,
  Camera,
  Pencil,
  Trash2,
  Lock,
  Settings2,
  X,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────
const TIER_OPTIONS = [
  { label: "Free",   value: "FREE"   },
  { label: "Paid",   value: "PAID"   },
  { label: "Hybrid", value: "HYBRID" },
];

// ─── Normalize ────────────────────────────────────────────────────────────────
function normalize(service) {
  const tier = service.tier ?? (service.paid || service.isPaid ? "PAID" : "FREE");
  const subServices = (service.subServices ?? [])
    .filter((s) => s.key != null)
    .map((s) => ({
      key:      typeof s.key === "object" ? (s.key?.name ?? JSON.stringify(s.key)) : String(s.key),
      isPaid:   s.isPaid   ?? s.paid   ?? false,
      isActive: s.isActive ?? s.active ?? false,
    }));
  return {
    ...service,
    tier,
    subServices,
    icon:         service.icon         ?? "",
    shortDesc:    service.shortDesc    ?? "",
    serviceKey:   service.serviceKey   ?? "",
    serviceName:  service.serviceName  ?? "",
    credit:       service.credit       ?? 0,
    displayOrder: service.displayOrder ?? 0,
    active:       service.active       ?? false,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function validateForm(form) {
  if (form.tier !== "FREE" && Number(form.credit) <= 0) {
    return "Credit cost must be > 0 for paid/hybrid";
  }
  return null;
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function ServiceToggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: value ? "#01696f" : "var(--border)",
        border: `1px solid ${value ? "#01696f" : "var(--border)"}`,
        position: "relative", cursor: "pointer", flexShrink: 0,
        transition: "background 0.2s ease",
      }}
    >
      <div style={{
        position: "absolute", top: 2,
        left: value ? 22 : 2,
        width: 18, height: 18, borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        transition: "left 0.2s ease",
      }} />
    </div>
  );
}

// ─── Tier Dot ─────────────────────────────────────────────────────────────────
function TierDot({ tier }) {
  const colors = { FREE: "#10b981", PAID: "#01696f", HYBRID: "#d97706" };
  return (
    <div style={{
      position: "absolute", top: 4, right: 4,
      width: 20, height: 20, borderRadius: "50%",
      background: colors[tier] ?? "#6b7280",
      color: "#fff", fontSize: 10, fontWeight: 700,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {(tier ?? "?")[0]}
    </div>
  );
}

// ─── Icon Field ───────────────────────────────────────────────────────────────
function IconField({ src, onUpload, onDelete, uploading, deleting, canUpload }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
        Service Icon
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{
          width: 56, height: 56, borderRadius: 10, overflow: "hidden", flexShrink: 0,
          border: "2px dashed var(--border)", background: "var(--bg-card)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {src ? (
            <img src={src} alt="icon" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <Camera size={22} strokeWidth={1.5} color="var(--text-muted)" />
          )}
        </div>

        {canUpload && (
          <label style={{
            padding: "7px 14px", borderRadius: "var(--radius)",
            border: "1px solid var(--border)", fontSize: 12, fontWeight: 600,
            cursor: uploading || deleting ? "not-allowed" : "pointer",
            color: "var(--text-secondary)", background: "var(--bg-card)",
            opacity: uploading || deleting ? 0.6 : 1, whiteSpace: "nowrap",
          }}>
            {uploading ? "Uploading…" : "Choose file…"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onUpload}
              disabled={uploading || deleting}
              style={{ display: "none" }}
            />
          </label>
        )}

        {!!src && (
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting || uploading}
            style={{
              padding: "7px 12px", borderRadius: "var(--radius)",
              border: "1px solid rgba(220,38,38,0.25)", fontSize: 12, fontWeight: 600,
              cursor: deleting || uploading ? "not-allowed" : "pointer",
              color: "#dc2626", background: "rgba(220,38,38,0.06)",
              opacity: deleting || uploading ? 0.6 : 1,
              display: "flex", alignItems: "center", gap: 5,
            }}
          >
            <Trash2 size={13} strokeWidth={2} />
            {deleting ? "Removing…" : "Remove"}
          </button>
        )}
      </div>

      {!canUpload && (
        <div style={{
          fontSize: 11, color: "var(--text-muted)",
          padding: "6px 10px", borderRadius: 6,
          background: "rgba(1,105,111,0.04)",
          border: "1px dashed rgba(1,105,111,0.2)",
        }}>
          File upload available after first save.
        </div>
      )}
    </div>
  );
}

// ─── Sub-service Row ──────────────────────────────────────────────────────────
function SubServiceRow({ sub, onChange }) {
  const keyStr = sub.key ?? "";
  const label =
    keyStr.toLowerCase().split("_").slice(1)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ") || keyStr;

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "9px 12px", borderRadius: 8,
      background: "var(--bg-card)", border: "1px solid var(--border)",
    }}>
      <div style={{ flex: 1, paddingRight: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>
          {label || "—"}
        </div>
        <div style={{ fontSize: 10, color: "var(--text-muted)", fontFamily: "monospace", marginTop: 2 }}>
          {keyStr}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600 }}>PAID</span>
          <ServiceToggle value={!!sub.isPaid} onChange={(v) => onChange({ ...sub, isPaid: v })} />
        </div>
      </div>
    </div>
  );
}

// ─── Sub-services Accordion ───────────────────────────────────────────────────
function SubServicesAccordion({ subServices, onChange }) {
  const [open, setOpen] = useState(false);
  const paidCount   = subServices.filter((s) => s.isPaid).length;
  const activeCount = subServices.filter((s) => s.isActive).length;

  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", padding: "11px 14px",
          background: "var(--bg-card)", border: "none", cursor: "pointer",
          borderBottom: open ? "1px solid var(--border)" : "none",
          transition: "background 0.15s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>
            Sub-services
          </span>
          <span style={{
            fontSize: 10, padding: "2px 8px", borderRadius: 20,
            background: "rgba(1,105,111,0.08)", color: "#01696f",
            border: "1px solid rgba(1,105,111,0.15)", fontWeight: 600,
          }}>
            {subServices.length} total · {paidCount} paid · {activeCount} active
          </span>
        </div>
        {open
          ? <ChevronUp size={14} strokeWidth={2.5} color="#01696f" />
          : <ChevronDown size={14} strokeWidth={2.5} color="#01696f" />
        }
      </button>

      {open && (
        <div style={{
          display: "flex", flexDirection: "column", gap: 6, padding: 12,
          background: "rgba(1,105,111,0.02)", maxHeight: 300, overflowY: "auto",
        }}>
          <div style={{ fontSize: 10, color: "var(--text-muted)", textAlign: "right", marginBottom: 4 }}>
            Tier auto-derives from paid flags
          </div>
          {subServices.map((sub) => (
            <SubServiceRow key={sub.key} sub={sub} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
function ServiceModal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        zIndex: 1000, overflowY: "auto", padding: "24px 16px",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 14, padding: 28,
          maxWidth: 540, width: "100%", margin: "0 auto",
          maxHeight: "calc(100vh - 48px)", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: "1px solid var(--border)", position: "relative",
        }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 20, position: "sticky", top: 0,
          background: "var(--bg-card)", zIndex: 2, paddingBottom: 10,
        }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 16, color: "var(--text-primary)", margin: 0 }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              width: 32, height: 32, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-muted)",
            }}
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── buildTranslationMap ──────────────────────────────────────────────────────
function buildTranslationMap(list, selectedLanguageId) {
  const map = {};
  (Array.isArray(list) ? list : []).forEach((item) => {
    const serviceId = item.id ?? item.serviceId;
    if (!serviceId) return;
    const matchedTranslation = (item.translations ?? []).find(
      (t) => t.languageId === selectedLanguageId
    );
    map[String(serviceId)] = {
      serviceName: matchedTranslation?.serviceName ?? item.translatedName ?? item.serviceName ?? null,
      shortDesc:   matchedTranslation?.shortDesc   ?? item.shortDesc      ?? null,
    };
  });
  return map;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ServiceManagement() {
  const [pendingIconFile,    setPendingIconFile]    = useState(null);
  const [pendingIconPreview, setPendingIconPreview] = useState(null);
  const [confirmRemoveIcon,  setConfirmRemoveIcon]  = useState(false);

  const [services,     setServices]     = useState([]);
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [showModal,    setShowModal]    = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState({});
  const [subServices,  setSubServices]  = useState([]);
  const [uploading,    setUploading]    = useState(false);
  const [deletingIcon, setDeletingIcon] = useState(false);
  const [dragIndex,    setDragIndex]    = useState(null);
  const [dragOverIndex,setDragOverIndex]= useState(null);

  const [languages,           setLanguages]           = useState([]);
  const [selectedLanguageId,  setSelectedLanguageId]  = useState("");
  const [translationMap,      setTranslationMap]      = useState({});
  const [loadingTranslations, setLoadingTranslations] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    serviceApi
      .getServices()
      .then((r) => {
        const raw  = r.data.data?.content ?? r.data.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        setTotal(list.length);
        const normalized = list.map(normalize);
        const sorted = [...normalized].sort((a, b) => {
          if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
          return (b.active ? 1 : 0) - (a.active ? 1 : 0);
        });
        setServices(sorted);
      })
      .catch(() => { setServices([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/languages")
      .then((r) => {
        const list = r.data.data ?? r.data ?? [];
        setLanguages(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!selectedLanguageId) { setTranslationMap({}); return; }
    setLoadingTranslations(true);
    serviceApi
      .getServicesWithTranslations(selectedLanguageId)
      .then((r) => {
        const list = r.data.data ?? r.data ?? [];
        setTranslationMap(buildTranslationMap(list, selectedLanguageId));
      })
      .catch(() => setTranslationMap({}))
      .finally(() => setLoadingTranslations(false));
  }, [selectedLanguageId]);

  const getDisplayValues = (service) => {
    if (!selectedLanguageId) {
      return { serviceName: service.serviceName, shortDesc: service.shortDesc, isTranslated: false };
    }
    const t = translationMap[String(service.id ?? "")];
    return {
      serviceName:  t?.serviceName || service.serviceName,
      shortDesc:    t?.shortDesc   || service.shortDesc,
      isTranslated: !!(t?.serviceName || t?.shortDesc),
    };
  };

  const openEdit = (service) => {
    setEditing(service.id);
    setForm({
      serviceName:  service.serviceName  ?? "",
      serviceKey:   service.serviceKey   ?? "",
      icon:         service.icon         ?? "",
      shortDesc:    service.shortDesc    ?? "",
      tier:         service.serviceKey === "HOROSCOPE" ? "FREE" : (service.tier ?? "FREE"),
      credit:       service.credit       ?? 0,
      displayOrder: service.displayOrder ?? 0,
      active:       !!service.active,
    });
    setSubServices(service.subServices ?? []);
    setShowModal(true);
  };

  const handleFormChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      showToast("Only JPG, PNG, and WEBP allowed", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast("Image must be under 5MB", "error");
      return;
    }
    setPendingIconFile(file);
    setPendingIconPreview(URL.createObjectURL(file));
    e.target.value = "";
  };

  const handleIconDelete = () => {
    if (!editing || !form.icon) return;
    setConfirmRemoveIcon(true);
  };

  const confirmIconDelete = async () => {
    setConfirmRemoveIcon(false);
    try {
      setDeletingIcon(true);
      await serviceApi.deleteServiceIcon(editing);
      setForm((prev) => ({ ...prev, icon: "" }));
      setServices((prev) => prev.map((s) => (s.id === editing ? { ...s, icon: "" } : s)));
      showToast("Icon removed");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to remove icon", "error");
    } finally {
      setDeletingIcon(false);
    }
  };

  const handleSubServiceChange = async (updatedSub) => {
    const previous = subServices;
    setSubServices((prev) => prev.map((s) => (s.key === updatedSub.key ? updatedSub : s)));
    try {
      await serviceApi.toggleSubService(editing, {
        key:      updatedSub.key,
        isPaid:   updatedSub.isPaid,
        isActive: updatedSub.isActive,
      });
      showToast(`${updatedSub.key} updated`);
      load();
    } catch {
      showToast("Failed to update sub-service", "error");
      setSubServices(previous);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const err = validateForm(form);
    if (err) { showToast(err, "error"); return; }

    try {
      let iconUrl = form.icon;
      if (pendingIconFile) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", pendingIconFile);
        try {
          const res = await serviceApi.uploadServiceIcon(editing, fd);
          iconUrl = res.data?.data?.icon ?? form.icon;
          if (iconUrl) setForm((prev) => ({ ...prev, icon: iconUrl }));
        } catch (uploadErr) {
          showToast(uploadErr.response?.data?.message || "Failed to upload icon", "error");
          setUploading(false);
          return;
        } finally {
          setUploading(false);
        }
      }

      await serviceApi.updateService(editing, {
        serviceName:  form.serviceName,
        serviceKey:   form.serviceKey,
        icon:         iconUrl,
        shortDesc:    form.shortDesc,
        tier:         form.tier,
        credit:       form.tier === "FREE" ? 0 : Number(form.credit ?? 0),
        displayOrder: Number(form.displayOrder ?? 0),
        active:       !!form.active,
      });

      showToast("Service updated");
      setPendingIconFile(null);
      if (pendingIconPreview) URL.revokeObjectURL(pendingIconPreview);
      setPendingIconPreview(null);
      setShowModal(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.message || "Something went wrong", "error");
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await serviceApi.toggleService(service.id, !service.active);
      showToast(`Service ${!service.active ? "activated" : "deactivated"}`);
      load();
    } catch {
      showToast("Failed to update status", "error");
    }
  };

  const handleDragStart = (e, i) => { setDragIndex(i); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver  = (e, i) => { e.preventDefault(); setDragOverIndex(i); };
  const handleDrop = async (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    const reordered = [...services];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(i, 0, moved);
    setServices(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
    try {
      await serviceApi.reorderServices(reordered.map((s) => s.id));
      showToast("Order saved");
    } catch {
      showToast("Failed to reorder", "error");
      load();
    }
  };

  const selectedLangName = languages.find((l) => l.id === selectedLanguageId)?.name ?? "";

  return (
    <div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .svc-drag-over {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(1,105,111,0.15) !important;
        }
      `}</style>

      {/* Page header row */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 4 }}>
        <PageHeader
          title="Service Management"
          subtitle="Configure service icons, pricing, and sub-service toggles"
        />

        {/* Language selector */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, paddingTop: 4 }}>
          {loadingTranslations && (
            <div style={{
              width: 14, height: 14, borderRadius: "50%",
              border: "2px solid var(--border)", borderTop: "2px solid #01696f",
              animation: "spin 0.7s linear infinite", flexShrink: 0,
            }} />
          )}
          <span style={{ fontSize: 13, color: "var(--text-secondary)", fontWeight: 600, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Globe size={14} strokeWidth={2} color="var(--text-secondary)" /> View in:
          </span>
          <select
            value={selectedLanguageId}
            onChange={(e) => setSelectedLanguageId(e.target.value)}
            style={{
              padding: "7px 12px", borderRadius: "var(--radius)",
              border: `1px solid ${selectedLanguageId ? "#01696f" : "var(--border)"}`,
              background: selectedLanguageId ? "rgba(1,105,111,0.06)" : "var(--bg-input)",
              color: "var(--text-primary)",
              fontSize: 13, outline: "none", cursor: "pointer", minWidth: 160,
              fontWeight: selectedLanguageId ? 600 : 400,
              transition: "border-color 0.2s, background 0.2s",
            }}
          >
            <option value="">Default (Original)</option>
            {languages.filter((l) => l.active).map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>
        {loading ? "Loading…" : `${total} service${total !== 1 ? "s" : ""}`}
        {selectedLanguageId && !loadingTranslations && (
          <span style={{
            marginLeft: 10, fontSize: 11, fontWeight: 600,
            color: "#01696f", background: "rgba(1,105,111,0.08)",
            border: "1px solid rgba(1,105,111,0.2)",
            padding: "2px 8px", borderRadius: 20,
            display: "inline-flex", alignItems: "center", gap: 4,
          }}>
            <Globe size={10} strokeWidth={2.5} /> {selectedLangName}
          </span>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>
          <div style={{
            width: 32, height: 32, margin: "0 auto 12px",
            border: "3px solid var(--border)", borderTop: "3px solid #01696f",
            borderRadius: "50%", animation: "spin 0.7s linear infinite",
          }} />
          Loading services…
        </div>
      ) : services.length === 0 ? (
        <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
            <Settings2 size={32} strokeWidth={1.5} color="var(--text-muted)" />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 6, color: "var(--text-secondary)" }}>No services found</div>
          <div style={{ fontSize: 12 }}>Services are seeded from the backend.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
          {services.map((service, index) => {
            const { serviceName, shortDesc, isTranslated } = getDisplayValues(service);

            return (
              <Card
                key={service.id ?? index}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e)  => handleDragOver(e, index)}
                onDrop={(e)      => handleDrop(e, index)}
                onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                className={dragOverIndex === index ? "svc-drag-over" : ""}
                style={{
                  cursor: "grab",
                  opacity: dragIndex !== null && dragIndex !== index ? 0.5 : 1,
                  transition: "all 0.2s ease",
                  display: "flex", flexDirection: "column",
                  borderLeft: isTranslated ? "3px solid rgba(1,105,111,0.4)" : undefined,
                }}
              >
                {/* Icon */}
                <div style={{ height: 72, marginBottom: 12, position: "relative" }}>
                  {service.icon ? (
                    <img
                      src={service.icon}
                      alt={service.serviceName}
                      onError={(e) => { e.target.style.display = "none"; }}
                      style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    />
                  ) : (
                    <div style={{
                      width: 72, height: 72, borderRadius: 12,
                      background: "var(--bg-card)", border: "2px dashed var(--border)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Smartphone size={24} strokeWidth={1.5} color="var(--text-muted)" />
                    </div>
                  )}
                  <TierDot tier={service.tier} />
                </div>

                {/* Name + badge */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                    <div style={{
                      fontFamily: "var(--font-display)", fontSize: 15,
                      color: "var(--text-primary)", marginBottom: 2,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {serviceName || "—"}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {service.serviceKey && (
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          {service.serviceKey}
                        </div>
                      )}
                      {selectedLanguageId && (
                        <span style={{
                          fontSize: 10, fontWeight: 600,
                          padding: "1px 6px", borderRadius: 10,
                          background: isTranslated ? "rgba(1,105,111,0.1)" : "rgba(107,114,128,0.08)",
                          color:      isTranslated ? "#01696f"              : "var(--text-muted)",
                          border:     `1px solid ${isTranslated ? "rgba(1,105,111,0.2)" : "rgba(107,114,128,0.15)"}`,
                          whiteSpace: "nowrap",
                          display: "inline-flex", alignItems: "center", gap: 3,
                        }}>
                          <Globe size={9} strokeWidth={2.5} />
                          {isTranslated ? selectedLangName : "No translation"}
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge variant={service.active ? "success" : "danger"} style={{ flexShrink: 0 }}>
                    {service.active ? "Active" : "Inactive"}
                  </Badge>
                </div>

                {/* Description */}
                <div style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 12, flex: 1, marginTop: 4 }}>
                  {shortDesc || "No description"}
                </div>

                {/* Tier + credits */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto", marginBottom: 10, minHeight: 28 }}>
                  {service.tier === "FREE" ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#10b981", background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", padding: "3px 10px", borderRadius: 20 }}>FREE</span>
                  ) : service.tier === "HYBRID" ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#d97706", background: "rgba(217,119,6,0.1)", border: "1px solid rgba(217,119,6,0.25)", padding: "3px 10px", borderRadius: 20 }}>HYBRID</span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#01696f", background: "rgba(1,105,111,0.1)", border: "1px solid rgba(1,105,111,0.25)", padding: "3px 10px", borderRadius: 20 }}>PAID</span>
                  )}
                  <span style={{ fontSize: 16, fontWeight: 700, color: "var(--text-primary)" }}>
                    {service.tier === "FREE" ? "0 credits" : `${service.credit} credits`}
                  </span>
                </div>

                {/* Sub-services pill */}
                <div style={{ marginBottom: 10, minHeight: 30 }}>
                  {service.subServices.length > 0 && (
                    <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "rgba(1,105,111,0.08)", color: "#01696f", border: "1px solid rgba(1,105,111,0.15)", fontWeight: 600 }}>
                      {service.subServices.length} sub-service{service.subServices.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {/* Card footer */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid var(--border)", marginTop: "auto" }}>
                  <Button size="sm" variant="outline" onClick={() => openEdit(service)}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <Settings2 size={12} strokeWidth={2} /> Configure
                    </span>
                  </Button>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {service.active ? "Enabled" : "Disabled"}
                    </span>
                    <ServiceToggle value={service.active} onChange={() => handleToggleActive(service)} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Configure Modal */}
      <ServiceModal open={showModal} onClose={() => setShowModal(false)} title="Configure Service">
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ padding: "12px 14px", borderRadius: 8, background: "rgba(1,105,111,0.04)", border: "1px solid rgba(1,105,111,0.15)", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.4px" }}>Service Name</label>
              <input
                type="text"
                value={form.serviceName ?? ""}
                onChange={(e) => handleFormChange("serviceName", e.target.value)}
                style={{ padding: "7px 10px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 13, fontWeight: 600, fontFamily: "var(--font-display)", outline: "none", width: "100%" }}
                placeholder="Service name"
              />
            </div>
            {form.serviceKey && (
              <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-muted)", letterSpacing: "0.3px" }}>
                KEY: {form.serviceKey}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.4px" }}>Description</label>
              <textarea
                value={form.shortDesc ?? ""}
                onChange={(e) => handleFormChange("shortDesc", e.target.value)}
                rows={2}
                style={{ padding: "7px 10px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 12, lineHeight: 1.6, fontFamily: "var(--font-body)", outline: "none", width: "100%", resize: "vertical" }}
                placeholder="Short description"
              />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Tier</label>
            {form.serviceKey === "HOROSCOPE" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: "var(--radius)", border: "1px solid rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.06)" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#10b981", background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 20, padding: "2px 10px" }}>FREE</span>
                <span style={{ fontSize: 11, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Lock size={11} strokeWidth={2} /> Horoscope is always free — tier cannot be changed
                </span>
              </div>
            ) : (
              <select
                value={form.tier ?? "FREE"}
                onChange={(e) => handleFormChange("tier", e.target.value)}
                style={{ width: "100%", padding: "9px 12px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", outline: "none", cursor: "pointer" }}
              >
                {TIER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          {form.tier !== "FREE" && (
            <Input
              label="Credit Cost"
              type="number"
              min="1"
              value={form.credit === undefined || form.credit === null ? "" : form.credit}
              onChange={(e) =>
                handleFormChange("credit", e.target.value === "" ? "" : Number(e.target.value))
              }
              required
            />
          )}

          <IconField
            src={pendingIconPreview || form.icon}
            onUpload={handleIconUpload}
            onDelete={handleIconDelete}
            uploading={uploading}
            deleting={deletingIcon}
            canUpload={!!editing}
          />

          {subServices.length > 0 ? (
            <SubServicesAccordion subServices={subServices} onChange={handleSubServiceChange} />
          ) : (
            <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center", padding: 12, borderRadius: 8, border: "1px dashed var(--border)" }}>
              No sub-services found for this service.
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderTop: "1px solid var(--border)" }}>
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>Active</span>
            <ServiceToggle value={!!form.active} onChange={(v) => handleFormChange("active", v)} />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button variant="ghost" type="button" onClick={() => {
              setShowModal(false);
              setPendingIconFile(null);
              if (pendingIconPreview) URL.revokeObjectURL(pendingIconPreview);
              setPendingIconPreview(null);
            }}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading || deletingIcon}>
              {uploading ? "Uploading…" : deletingIcon ? "Removing…" : "Save Changes"}
            </Button>
          </div>
        </form>
      </ServiceModal>

      {/* Confirm Remove Icon */}
      {confirmRemoveIcon && (
        <div
          onClick={() => setConfirmRemoveIcon(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--bg-card)", borderRadius: 12, padding: 24, maxWidth: 360, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", border: "1px solid var(--border)" }}
          >
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
              <Trash2 size={28} strokeWidth={1.5} color="#dc2626" />
            </div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)", textAlign: "center", marginBottom: 8 }}>Remove Service Icon?</h3>
            <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.6, marginBottom: 20 }}>
              Do you want to remove this image? This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => setConfirmRemoveIcon(false)}
                style={{ padding: "8px 20px", borderRadius: "var(--radius)", border: "1px solid var(--border)", background: "var(--bg-card)", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "var(--text-secondary)" }}
              >
                Cancel
              </button>
              <button
                onClick={confirmIconDelete}
                style={{ padding: "8px 20px", borderRadius: "var(--radius)", border: "none", background: "#dc2626", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff" }}
              >
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}